import { ComponentType, lazy } from 'react';

const RELOAD_FLAG = 'agio:stale-chunk-reloaded';
export const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  // Safari's wording when an HTML error page is served in place of the chunk
  'is not a valid JavaScript MIME type',
  'Unable to preload CSS',
];

// mirrors React.lazy's own constraint, which is what keeps the component's props intact
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LazyComponent = ComponentType<any>;

type LazyModule<T extends LazyComponent> = { default: T };

export const isStaleChunkError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);

  return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

// A failure within the cooldown of a reload means the new build is genuinely broken, so the
// error must reach Sentry instead of looping. A failure after it is a tab that outlived
// another deployment, which a fresh reload fixes.
const hasAlreadyReloaded = () => {
  try {
    const reloadedAt = Number(sessionStorage.getItem(RELOAD_FLAG));

    return Date.now() - reloadedAt < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const markReloaded = () => {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* storage unavailable in private modes; the reload still goes ahead */
  }
};

let reloadInFlight = false;

const reloadOnceForStaleChunk = () => {
  // A reload already underway counts as handled, so callers keep showing a loader
  // rather than surfacing an error for a page that is about to be replaced.
  if (reloadInFlight) return true;
  if (hasAlreadyReloaded()) return false;

  reloadInFlight = true;
  markReloaded();
  window.location.reload();

  return true;
};

// Vite dispatches `vite:preloadError` synchronously before rethrowing, so recovery has
// already been decided by the time a rejection handler runs.
export const isStaleChunkRecovering = () => reloadInFlight;

export const swallowStaleChunkError = (error: unknown) => {
  if (!isStaleChunkError(error)) throw error;
};

export const lazyWithReload = <T extends LazyComponent>(factory: () => Promise<LazyModule<T>>) =>
  lazy(() =>
    factory().catch((error: unknown) => {
      // Hold Suspense on its loader while the reload takes over, so the user
      // never sees an error flash for a recoverable failure.
      if (isStaleChunkError(error) && reloadOnceForStaleChunk()) {
        return new Promise<LazyModule<T>>(() => {});
      }

      throw error;
    }),
  );

export const registerChunkRecovery = () => {
  // Never preventDefault here: Vite's preload catch then resolves undefined and
  // React.lazy reads undefined.default.
  window.addEventListener('vite:preloadError', event => {
    if (isStaleChunkError(event.payload)) reloadOnceForStaleChunk();
  });
};
